import { extractYouTubeID } from './string';

/**
 * By default, React DOM escapes any values embedded in JSX before rendering them,
 * but sometimes it is necessary to sanitize the user-generated content of received entities.
 * If you use this data in component props without any sanitization or encoding,
 * it might create XSS vulnerabilities.
 *
 * You should especially consider how you are using extended data inside the app.
 */

const ESCAPE_TEXT_REGEXP = /[<>]/g;
const ESCAPE_TEXT_REPLACEMENTS = {
  //fullwidth lesser-than character
  '<': '\uff1c',
  //fullwidth greater-than character
  '>': '\uff1e',
};

const ALLOWED_HTML_TAGS = new Set([
  'p',
  'strong',
  'em',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'blockquote',
  'br',
  'hr',
]);
const ALLOWED_HTML_TAG_REGEX = /<\/?(p|strong|em|u|s|ul|ol|li|blockquote|br|hr)\b[^>]*>/i;
const DANGEROUS_ELEMENTS_REGEX = /<(script|style|iframe|object|embed|form|input|textarea|button|link|meta|base|applet|svg|math)\b[^>]*>[\s\S]*?<\/\1>/gi;
// One tag, or one lone angle bracket. `[^<>]` keeps a tag from swallowing a nested `<`, so the
// scan below sees `<<b>img …>` as `<`, `<b>`, text, `>` — never as one tag.
const TAG_OR_BRACKET_REGEX = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^<>]*>|[<>]/g;
const ESCAPE_HTML_BRACKETS = { '<': '&lt;', '>': '&gt;' };

// An example how you could sanitize text content.
// This swaps some coding related characters to less dangerous ones
export const sanitizeText = str =>
  str == null
    ? str
    : typeof str === 'string'
    ? str.replace(ESCAPE_TEXT_REGEXP, ch => ESCAPE_TEXT_REPLACEMENTS[ch])
    : '';

/**
 * Convert HTML-like content to plain text for validation/storage use cases.
 *
 * @param {string} html
 * @returns {string}
 */
export const stripHtmlTags = html => {
  if (html == null) return '';
  if (typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

// Elements whose content is never text a reader would see.
const NON_TEXT_ELEMENTS_REGEX = /<(script|style|template)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
// Tags that end a line or a paragraph when the HTML is rendered — opening AND closing ones, so
// "Enthalten:<ul><li>Monitor" separates even without a closing </p> before the list.
const BLOCK_TAG_REGEX = /<\/?(?:p|div|li|ul|ol|h[1-6]|tr|td|th|table|blockquote|section|article|header|footer|br|hr)\b[^>]*>/gi;
const ANY_TAG_REGEX = /<\/?[a-zA-Z][^<>]*>|<!--[\s\S]*?-->/g;
const BLOCK_BREAK = '\u0000';
const LINE_BREAK_REGEX = /\r\n|\r|\n/g;
// A segment that already ends a sentence or a clause gets a plain space before the next one.
const ENDS_WITH_PUNCTUATION_REGEX = /[.!?:;,…\-–—]["'»«“”‘’)\]]*$/;
// "Präsentationen.In jeder Buchung" — a full stop between a lower-case and an upper-case letter
// with no space is a paragraph boundary that got lost before it reached us (plain-text
// descriptions pasted from elsewhere). Digits are left alone ("CHF 2.50"), so are domains
// ("cowork24.ch": lower case after the dot).
const GLUED_SENTENCE_REGEX = /([a-zäöüß])([.!?])([A-ZÄÖÜ])/g;
const NAMED_ENTITIES = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  shy: '',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  laquo: '«',
  raquo: '»',
  lsquo: '‘',
  rsquo: '’',
  sbquo: '‚',
  ldquo: '“',
  rdquo: '”',
  bdquo: '„',
  bull: '•',
  middot: '·',
  euro: '€',
  deg: '°',
  copy: '©',
  reg: '®',
  trade: '™',
  auml: 'ä',
  ouml: 'ö',
  uuml: 'ü',
  Auml: 'Ä',
  Ouml: 'Ö',
  Uuml: 'Ü',
  szlig: 'ß',
  agrave: 'à',
  aacute: 'á',
  acirc: 'â',
  egrave: 'è',
  eacute: 'é',
  ecirc: 'ê',
  igrave: 'ì',
  iacute: 'í',
  ocirc: 'ô',
  ugrave: 'ù',
  ccedil: 'ç',
  Eacute: 'É',
};
const ENTITY_REGEX = /&(#[xX][0-9a-fA-F]+|#[0-9]+|[a-zA-Z][a-zA-Z0-9]*);/g;
// "&amp;amp;" is HTML escaped twice before it was stored; three rounds undo anything a real
// description has been seen to carry, and the cap keeps a hostile string from looping.
const MAX_ENTITY_DECODE_ROUNDS = 3;

const decodeEntityMatch = (match, entity) => {
  if (entity[0] === '#') {
    const isHex = entity[1] === 'x' || entity[1] === 'X';
    const codePoint = isHex ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
    const isValid =
      Number.isInteger(codePoint) &&
      codePoint > 0 &&
      codePoint <= 0x10ffff &&
      !(codePoint >= 0xd800 && codePoint <= 0xdfff);
    return isValid ? String.fromCodePoint(codePoint) : match;
  }
  return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, entity)
    ? NAMED_ENTITIES[entity]
    : match;
};

const decodeHtmlEntities = str => {
  let current = str;
  for (let round = 0; round < MAX_ENTITY_DECODE_ROUNDS; round++) {
    const decoded = current.replace(ENTITY_REGEX, decodeEntityMatch);
    if (decoded === current) {
      break;
    }
    current = decoded;
  }
  return current;
};

/**
 * Turn a listing description (rich-text HTML or plain text with line breaks) into one line of
 * readable plain text — for `<meta name="description">`, Open Graph and JSON-LD.
 *
 * `stripHtmlTags` is not that: it deletes tags without leaving a space and keeps entities, so
 * "<p>zur Limmat.</p><p>Hier kannst du</p>" came out as "zur Limmat.Hier kannst du", and
 * "Workshops &amp; Coachings" reached the search snippet as "Workshops &amp;amp; Coachings" once
 * React escaped it again. It stays as it is — the rich-text editor's validation relies on it.
 *
 * Rules:
 * - script/style content is dropped;
 * - a block boundary (p, div, li, headings, br, table rows, …) and a line break become a space,
 *   or ". " when the text before it does not end in punctuation ("Ausstattung</h3><p>WLAN" →
 *   "Ausstattung. WLAN");
 * - every other tag is removed, entities are decoded (named, decimal, hex; repeatedly for
 *   double-escaped input), and tags that only appear after decoding are removed too;
 * - whitespace (including no-break spaces) collapses to single spaces.
 *
 * The result is plain text, not HTML: callers that put it into markup must escape it as usual
 * (React and Helmet do).
 *
 * @param {string} html
 * @returns {string} plain text, '' for anything that is not a string
 */
export const htmlToPlainText = html => {
  if (typeof html !== 'string' || html.length === 0) {
    return '';
  }

  const withBreaks = html
    .replace(NON_TEXT_ELEMENTS_REGEX, ' ')
    .replace(BLOCK_TAG_REGEX, BLOCK_BREAK)
    .replace(LINE_BREAK_REGEX, BLOCK_BREAK)
    .replace(ANY_TAG_REGEX, '');

  const decoded = decodeHtmlEntities(withBreaks)
    // Markup that was escaped in storage is markup after decoding — not text to show.
    .replace(ANY_TAG_REGEX, '');

  const text = decoded
    .split(BLOCK_BREAK)
    .map(segment => segment.replace(/\s+/g, ' ').trim())
    .filter(segment => segment.length > 0)
    .reduce((result, segment) => {
      if (!result) {
        return segment;
      }
      const separator = ENDS_WITH_PUNCTUATION_REGEX.test(result) ? ' ' : '. ';
      return `${result}${separator}${segment}`;
    }, '');

  return text.replace(GLUED_SENTENCE_REGEX, '$1$2 $3');
};

/**
 * Sanitize HTML using an allowlist of safe tags produced by the rich text editor.
 * Strips all attributes, removes dangerous elements (script, iframe, etc.) entirely,
 * and only preserves formatting tags like p, strong, em, ul, ol, li, etc.
 * Falls back to sanitizeText for non-HTML strings.
 *
 * The output goes straight into dangerouslySetInnerHTML (listing description, transaction
 * messages), so it has one invariant: every `<` in it belongs to a canonical, attribute-free
 * allowlisted tag that this function wrote itself. Everything is decided in ONE left-to-right scan
 * of the input; a lone `<` or `>` is escaped, never removed. The previous version deleted
 * disallowed tags with a regex, and deleting `<b>` from `<<b>img src=x onerror=…>` assembled a
 * brand-new `<img>` that no earlier pass had seen — stored XSS from any listing description.
 *
 * @param {string} html
 * @returns {string} sanitized HTML string
 */
export const sanitizeHtml = html => {
  if (html == null) return '';
  if (typeof html !== 'string') return '';
  const hasAllowedHtmlTag = ALLOWED_HTML_TAG_REGEX.test(html);

  if (!hasAllowedHtmlTag) return sanitizeText(html);

  // Cosmetic only (drops the text inside e.g. <script>…</script> instead of showing it); safety
  // comes from the scan below, whatever this step leaves behind.
  const withoutDangerousElements = html.replace(DANGEROUS_ELEMENTS_REGEX, '');

  return withoutDangerousElements.replace(TAG_OR_BRACKET_REGEX, (match, tagName) => {
    if (!tagName) return ESCAPE_HTML_BRACKETS[match];
    const tag = tagName.toLowerCase();
    if (!ALLOWED_HTML_TAGS.has(tag)) return '';
    const isClosing = match.startsWith('</');
    return isClosing ? `</${tag}>` : `<${tag}>`;
  });
};

// Enum and multi-enum work with predefined option configuration
const sanitizeEnum = (str, options) => (options.map(o => `${o.option}`).includes(str) ? str : null);
const sanitizeMultiEnum = (arr, options) =>
  Array.isArray(arr)
    ? arr.reduce((ret, value) => {
        const enumValue = sanitizeEnum(value, options);
        return enumValue ? [...ret, enumValue] : ret;
      }, [])
    : [];
const sanitizeLong = lng => (lng == null || typeof lng === 'number' ? lng : null);
const sanitizeBoolean = bool => (bool == null || typeof bool === 'boolean' ? bool : null);

const sanitizeYoutubeVideoUrl = url => {
  const sanitizedUrl = sanitizeUrl(url);
  const videoID = extractYouTubeID(sanitizedUrl);
  return videoID ? `https://www.youtube.com/watch?v=${videoID}` : null;
};

// URL sanitizer. This code is adapted from
// https://github.com/braintree/sanitize-url/
// <sanitizeUrl>
const INVALID_PROTOCOL_REGEXP = /^([^\w]*)(javascript|data|vbscript)/im;
const HTML_ENTITIES_REGEXP = /&#(\w+)(^\w|;)?/g;
const CTRL_CHARACTERS_REGEXP = /[\u0000-\u001F\u007F-\u009F\u2000-\u200D\uFEFF]/gim;
const URL_SCHEME_REGEXP = /^([^:]+):/gm;
const RELATIVE_FIRST_CHARACTERS = ['.', '/'];

function isRelativeUrlWithoutProtocol(url) {
  return RELATIVE_FIRST_CHARACTERS.indexOf(url[0]) > -1;
}

// adapted from https://stackoverflow.com/a/29824550/2601552
function decodeHtmlCharacters(str) {
  return str.replace(HTML_ENTITIES_REGEXP, (match, dec) => {
    return String.fromCharCode(dec);
  });
}

export function sanitizeUrl(url) {
  const sanitizedUrl = decodeHtmlCharacters(url || '')
    .replace(CTRL_CHARACTERS_REGEXP, '')
    .trim();

  if (!sanitizedUrl) {
    return 'about:blank';
  }

  if (isRelativeUrlWithoutProtocol(sanitizedUrl)) {
    return sanitizedUrl;
  }

  const urlSchemeParseResults = sanitizedUrl.match(URL_SCHEME_REGEXP);

  if (!urlSchemeParseResults) {
    return sanitizedUrl;
  }

  const urlScheme = urlSchemeParseResults[0];

  if (INVALID_PROTOCOL_REGEXP.test(urlScheme)) {
    return 'about:blank';
  }

  return sanitizedUrl;
}
// </sanitizeUrl>

/**
 * Sanitize user entity.
 * If you add public data, you should probably sanitize it here.
 * By default, React DOM escapes any values embedded in JSX before rendering them,
 * but if you use this data on props, it might create XSS vulnerabilities
 * E.g. you should sanitize and encode URI if you are creating links from public data.
 */
export const sanitizeUser = (entity, config = {}) => {
  const { attributes, ...restEntity } = entity || {};
  const { profile, ...restAttributes } = attributes || {};
  const { bio, displayName, abbreviatedName, publicData = {}, metadata = {}, ...restProfile } =
    profile || {};

  const sanitizePublicData = publicData => {
    // TODO: If you add public data, you should probably sanitize it here.
    const sanitizedConfiguredPublicData = sanitizeConfiguredPublicData(publicData, config);
    return publicData ? { publicData: sanitizedConfiguredPublicData } : {};
  };
  // Note: this does NOT filter by config — unknown keys ARE passed through
  // (strings via sanitizeText, everything else untouched); the config only
  // decides HOW a known field is sanitized. The previous wording claimed the
  // opposite and sent anyone writing metadata via the Integration API hunting
  // for a config entry they never needed.
  //
  // profile.metadata.stripeVerified (the trust badge, written by
  // server/api/sync-stripe-status.js) relies on exactly this. Turning it into
  // an allowlist removes the badge marketplace-wide — guarded by
  // util/verifiedProvider.test.js.
  const sanitizeMetadata = metadata => {
    const sanitized = sanitizeConfiguredMetadata(metadata, config);
    return metadata ? { metadata: sanitized } : {};
  };

  const profileMaybe = profile
    ? {
        profile: {
          abbreviatedName: sanitizeText(abbreviatedName),
          displayName: sanitizeText(displayName),
          bio: sanitizeText(bio),
          ...sanitizePublicData(publicData),
          ...sanitizeMetadata(metadata),
          ...restProfile,
        },
      }
    : {};
  const attributesMaybe = attributes ? { attributes: { ...profileMaybe, ...restAttributes } } : {};

  return { ...attributesMaybe, ...restEntity };
};

/**
 * Sanitize extended data against configuration (against schemaType)
 * @param {any} value Any JSON value
 * @param {object} config containing "schemaType"
 * @returns sanitized value or null
 */
const sanitizedExtendedDataFields = (value, config) => {
  const { schemaType, enumOptions } = config;
  const sanitized =
    schemaType === 'shortText'
      ? sanitizeText(value)
      : schemaType === 'text'
      ? sanitizeText(value)
      : schemaType === 'enum'
      ? sanitizeEnum(value, enumOptions)
      : schemaType === 'multi-enum'
      ? sanitizeMultiEnum(value, enumOptions)
      : schemaType === 'long'
      ? sanitizeLong(value)
      : schemaType === 'boolean'
      ? sanitizeBoolean(value)
      : schemaType === 'youtubeVideoUrl'
      ? sanitizeYoutubeVideoUrl(value)
      : null;

  return sanitized;
};

/**
 * Some of the public data is configurable. This validates that data against the given config.
 * (The config paramter contains listingFields config.)
 *
 * NOTE: this does not handle nested JSON-like objects or other extra data,
 * but there's handling for string type content ('<' & '>' characters are replaced with full-width ones).
 *
 * @param {object} publicData
 * @param {*} config
 * @returns
 */
const sanitizeConfiguredPublicData = (publicData, config = {}) => {
  // The publicData could be null (e.g. for banned user)
  const publicDataObj = publicData || {};
  return Object.entries(publicDataObj).reduce((sanitized, entry) => {
    const [key, value] = entry;
    const foundListingFieldConfig = config?.listingFields?.find(d => d.key === key);
    const foundUserFieldConfig = config?.userFields?.find(d => d.key === key);
    const knownKeysWithString = [
      'listingType',
      'transactionProcessAlias',
      'unitType',
      'userType',
      'cardStyle',
    ];
    const sanitizedValue = knownKeysWithString.includes(key)
      ? sanitizeText(value)
      : foundListingFieldConfig
      ? sanitizedExtendedDataFields(value, foundListingFieldConfig)
      : foundUserFieldConfig
      ? sanitizedExtendedDataFields(value, foundUserFieldConfig)
      : typeof value === 'string'
      ? sanitizeText(value)
      : value;

    return {
      ...sanitized,
      [key]: sanitizedValue,
    };
  }, {});
};

const sanitizeConfiguredMetadata = (metadata, config = {}) => {
  const metadataObj = metadata || {};
  const listingMetadataConfigs = config?.listingFields?.filter(d => d.scope === 'metadata') || [];
  const userMetadataConfigs = config?.userFields?.filter(d => d.scope === 'metadata') || [];
  const metadataFieldConfigs = [...listingMetadataConfigs, ...userMetadataConfigs];

  return Object.entries(metadataObj).reduce((sanitized, entry) => {
    const [key, value] = entry;
    const foundFieldConfig = metadataFieldConfigs.find(d => d.key === key);
    const sanitizedValue = foundFieldConfig
      ? sanitizedExtendedDataFields(value, foundFieldConfig)
      : typeof value === 'string'
      ? sanitizeText(value)
      : value;

    return {
      ...sanitized,
      [key]: sanitizedValue,
    };
  }, {});
};

/**
 * Sanitize listing entity.
 * If you add public data, you should probably sanitize it here.
 * By default, React DOM escapes any values embedded in JSX before rendering them,
 * but if you use this data on props, it might create XSS vulnerabilities
 * E.g. you should sanitize and encode URI if you are creating links from public data.
 */
export const sanitizeListing = (entity, config = {}) => {
  const { attributes, ...restEntity } = entity;
  const { title, description, publicData, metadata, ...restAttributes } = attributes || {};

  const sanitizeLocation = location => {
    const { address, building } = location || {};
    return { address: sanitizeText(address), building: sanitizeText(building) };
  };

  const sanitizePublicData = publicData => {
    // Here's an example how you could sanitize location and rules from publicData:
    // TODO: If you add public data, you should probably sanitize it here.
    const { location, ...restPublicData } = publicData || {};
    const locationMaybe = location ? { location: sanitizeLocation(location) } : {};
    const sanitizedConfiguredPublicData = sanitizeConfiguredPublicData(restPublicData, config);

    return publicData ? { publicData: { ...locationMaybe, ...sanitizedConfiguredPublicData } } : {};
  };

  // Note: this does NOT filter by config — unknown keys ARE passed through
  // (strings via sanitizeText, everything else untouched); the config only
  // decides HOW a known field is sanitized. The previous wording claimed the
  // opposite and sent anyone writing metadata via the Integration API hunting
  // for a config entry they never needed.
  const sanitizeMetadata = metadata => {
    const sanitized = sanitizeConfiguredMetadata(metadata, config);
    return metadata ? { metadata: sanitized } : {};
  };

  const attributesMaybe = attributes
    ? {
        attributes: {
          title: sanitizeText(title),
          description: sanitizeHtml(description),
          ...sanitizePublicData(publicData),
          ...sanitizeMetadata(metadata),
          ...restAttributes,
        },
      }
    : {};

  return { ...attributesMaybe, ...restEntity };
};

/**
 * Sanitize entities if needed.
 * Remember to add your own sanitization rules for your extended data
 */
export const sanitizeEntity = (entity, config) => {
  const { type } = entity;
  switch (type) {
    case 'listing':
      return sanitizeListing(entity, config);
    case 'user':
      return sanitizeUser(entity, config);
    default:
      return entity;
  }
};
